import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import NewTransactionModal from '@/components/NewTransactionModal';
import { dekontuAc } from '@/lib/dekont';
import EkDugmesi from '@/components/HareketEkleri';
import WhatsAppDugmesi from '@/components/WhatsAppDugmesi';
import WhatsAppGonder from '@/components/WhatsAppGonder';
import KopyalaDugmesi from '@/components/KopyalaDugmesi';
import SearchableSelect from '@/components/SearchableSelect';
import { ekstreIndir } from '@/lib/ekstre';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin, Building2, Plus, Trash2, AlertTriangle, CreditCard, FileText, ExternalLink, BadgeCheck, Shield, Landmark, Pencil, X, Save, Receipt, FileSpreadsheet, FileDown, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { HESAP_TURLERI } from '@/lib/hesapTurleri';

const currencies = ['TRY', 'USD', 'EUR', 'GBP'];

// Türkçe sayı formatı: binlik ayraç nokta, ondalık virgül
const fmtTR = (n, decimals = 2) =>
  n == null ? '—' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

// Tür listesi tek yerde (lib/hesapTurleri.js). Burada bir dönem AYRI bir liste
// vardı ('Tedarikçi', 'Müşteri'...): kayıtlı tür listede olmadığı için kutu boş
// görünüyor, seçilen değer de öbür ekranların tanımadığı bir tür yazıyordu.
const accountTypes = HESAP_TURLERI;

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/suppliers');
  const { role, hasPermission } = useAuth();
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  // Ekstre hareketi kasa ucundan silinir (treasury), banka hesabı cari ucundan (suppliers).
  const hareketSilebilir = hasPermission('treasury', 'delete');
  const bankaSilebilir = hasPermission('suppliers', 'delete');
  const canEdit = role === 'super_admin' || role === 'admin';
  const [supplier, setSupplier] = useState(null);
  usePageTitle(supplier?.name || 'Cari Detayı');
  const [ledger, setLedger] = useState([]);
  const [currFilter, setCurrFilter] = useState('all');
  const [paidFilter, setPaidFilter] = useState('all');
  const [showInvoice, setShowInvoice] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showBank, setShowBank] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ invoice_number: '', invoice_date: '', due_date: '', currency: 'TRY', amount_foreign: '', exchange_rate: '1', notes: '' });
  const [bankForm, setBankForm] = useState({ bank_name: '', currency: 'TRY', iban: '', uban: '', account_holder: '' });
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [treasuryAccounts, setTreasuryAccounts] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  // Cari ekstresinde hareket düzenleme. Kayıt `supplier_ledger`'da tek doküman
  // olduğu için Kasa/Banka ekranındaki uç kullanılır; iki ekran aynı kod
  // yolundan geçsin, davranış ayrışmasın. Yetki o ucun GERÇEKTE istediği
  // `treasury/edit` (bir dönem yalnızca süper admin'di).
  const hareketDuzenleyebilir = hasPermission('treasury', 'edit');
  const [editEntry, setEditEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({});
  const [entrySaving, setEntrySaving] = useState(false);
  // Cari notu: firma adının yanında görünen tek bir kısa metin. Yazmak cari
  // bilgisini değiştirmekle aynı yetki (sunucu `suppliers/edit` ister).
  const notYazabilir = hasPermission('suppliers', 'edit');
  const [notAcik, setNotAcik] = useState(false);
  const [notMetni, setNotMetni] = useState('');
  const [notKaydediliyor, setNotKaydediliyor] = useState(false);

  // Fatura/irsaliye gibi bir BELGEDEN doğan satır burada düzenlenmez —
  // belgeyle ekstre birbirinden kopar. Onlarda satırdaki belge bağlantısı var.
  //
  // CARİ İKİ YÖNDE DE düzenleyebilir (kullanıcı, 2026-10-01): parayı ALDIĞI
  // satırda (`supplier_id`) olduğu gibi VERDİĞİ satırda da (`payment_account_id`).
  // Bir dönem yalnızca ilki açıktı: personelin şirket adına bir firmaya yaptığı
  // ödeme, personelin ekstresinde düzeltilemiyordu; Hareketler sayfası da
  // yalnızca bir tarafı kasa/banka olan kayıtları listelediği için hiçbir
  // ekrandan düzeltilemiyordu.
  const duzenlenebilir = (e) => hareketDuzenleyebilir && !e.invoice_id && !e.satis_fatura_id
    && (e.supplier_id === id || e.payment_account_id === id);

  // Hareketin İKİ TARAFI, Kasa/Banka ekranıyla aynı anlamda (treasury
  // `_ledger_to_txn_view`): ödeme/iade → para `supplier_id`ye GİRER,
  // `payment_account_id`den ÇIKAR; borç → `supplier_id`den çıkar.
  const hareketiAc = (e) => {
    const borc = e.type === 'debt';
    setEditEntry(e);
    setEntryForm({
      transaction_date: e.invoice_date || '',
      due_date: e.due_date || '',
      amount: String(e.amount_foreign ?? ''),
      currency: e.currency || 'TRY',
      description: e.notes || '',
      giren: borc ? '' : (e.supplier_id || ''),
      cikan: borc ? (e.supplier_id || '') : (e.payment_account_id || ''),
    });
  };

  const hareketiKaydet = async () => {
    if (!entryForm.giren && !entryForm.cikan) {
      toast.error('Giren ya da çıkan hesaplardan en az biri seçilmeli');
      return;
    }
    if (entryForm.giren && entryForm.giren === entryForm.cikan) {
      toast.error('Giren ve çıkan hesap aynı olamaz');
      return;
    }
    if (entryForm.giren !== id && entryForm.cikan !== id
        && !window.confirm('Bu cari iki taraftan da çıkarıldı; hareket bu ekstreden kaybolacak. Devam edilsin mi?')) {
      return;
    }
    setEntrySaving(true);
    try {
      await api.patch(`/treasury/transactions/${editEntry.id}`, {
        transaction_date: entryForm.transaction_date,
        due_date: entryForm.due_date || '',
        amount: parseFloat(String(entryForm.amount).replace(',', '.')) || 0,
        currency: entryForm.currency,
        description: entryForm.description,
        debit_account_id: entryForm.giren || '',
        credit_account_id: entryForm.cikan || '',
      });
      toast.success('Hareket güncellendi');
      setEditEntry(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncellenemedi');
    }
    setEntrySaving(false);
  };

  const hareketiSil = async (e) => {
    if (!window.confirm(`${e.invoice_date} tarihli ${fmtTR(e.amount_foreign)} ${e.currency} tutarındaki hareket silinecek. Emin misiniz?`)) return;
    try {
      await api.delete(`/treasury/transactions/${e.id}`);
      toast.success('Hareket silindi');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = useCallback(() => {
    api.get(`/suppliers/${id}`).then(r => setSupplier(r.data)).catch(() => toast.error('Yuklenemedi'));
    api.get(`/suppliers/${id}/ledger`).then(r => setLedger(r.data)).catch(() => {});
    api.get('/treasury/accounts').then(r => setTreasuryAccounts(r.data)).catch(() => {});
    api.get('/suppliers').then(r => {
      const u = []; const s = new Set();
      r.data.forEach(x => { if (!s.has(x.id)) { s.add(x.id); u.push(x); } });
      setAllSuppliers(u);
    }).catch(() => {});
  }, [id]);

  const startEdit = () => {
    setEditForm({
      name: supplier.name || '',
      code: supplier.code || '',
      account_type: supplier.account_type || '',
      phone: supplier.phone || '',
      authorized_phone: supplier.authorized_phone || '',
      email: supplier.email || '',
      tax_office: supplier.tax_office || '',
      tax_number: supplier.tax_number || '',
      address_street: supplier.address_street || '',
      address_district: supplier.address_district || '',
      address_city: supplier.address_city || '',
      address_country: supplier.address_country || '',
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    setLoading(true);
    try {
      await api.patch(`/suppliers/${id}`, editForm);
      toast.success('Tedarikçi güncellendi');
      setEditMode(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme hatası');
    }
    setLoading(false);
  };

  const notAc = () => {
    setNotMetni(supplier?.cari_notu || '');
    setNotAcik(true);
  };

  // Boş metin kaydetmek notu SİLER (sunucu da öyle davranır). Yanıt yerel
  // duruma yazılır: tüm sayfayı (ekstre, hesap listesi) yeniden çekmeye gerek yok.
  const notKaydet = async () => {
    setNotKaydediliyor(true);
    try {
      const r = await api.put(`/suppliers/${id}/not`, { not: notMetni });
      setSupplier(p => {
        const yeni = { ...p, ...r.data };
        if (!r.data.cari_notu) {
          delete yeni.cari_notu; delete yeni.cari_notu_guncelleyen; delete yeni.cari_notu_zaman;
        }
        return yeni;
      });
      setNotAcik(false);
      toast.success(r.data.cari_notu ? 'Not kaydedildi' : 'Not silindi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Not kaydedilemedi');
    }
    setNotKaydediliyor(false);
  };

  if (!supplier) return <div className="text-center py-8 text-[#8a7e70]">Yukleniyor...</div>;

  const now = new Date().toISOString().slice(0, 10);

  // Hareket düzenlemede iki tarafın seçenekleri: kasa/banka + bütün cariler
  // (personel dahil), hesap türüne göre gruplu. İlk seçenek tarafı BOŞ bırakır.
  const hesapSecenekleri = [
    { value: '', label: '— boş (tek taraflı) —' },
    ...[...treasuryAccounts.map(a => ({ ...a, account_type: a.account_type || 'Kasa ve Bankalar' })), ...allSuppliers]
      .filter(a => a && a.id)
      .sort((a, b) => (a.account_type || '').localeCompare(b.account_type || '', 'tr')
        || (a.name || '').localeCompare(b.name || '', 'tr'))
      .map(a => ({ value: a.id, label: a.name, hint: a.code || '', group: a.account_type || 'Diğer' })),
  ];

  // Bakiye: is_incoming → +, diğeri → − (tüm hesap tipleri için tek tip)
  // TOPLAM BORÇ / ALACAK (kullanıcı, 2026-10-07: KPI'a tıklanınca balon): bakiyeyi
  // oluşturan iki tarafın toplamı, AYNI işaret kuralından — eksi taraf Borç, artı
  // taraf Alacak, Bakiye = Alacak − Borç. Ayrı bir kural yazılsaydı balondaki iki
  // rakamın farkı kutudaki bakiyeyi vermeyebilirdi.
  const balances = {};
  const taraflar = {};
  ledger.forEach(e => {
    const c = e.currency || 'TRY';
    if (!balances[c]) balances[c] = 0;
    if (!taraflar[c]) taraflar[c] = { borc: 0, alacak: 0, borcAdet: 0, alacakAdet: 0 };
    const tutar = e.amount_foreign || 0;
    balances[c] += (e.is_incoming ? 1 : -1) * tutar;
    if (e.is_incoming) { taraflar[c].alacak += tutar; taraflar[c].alacakAdet += 1; }
    else { taraflar[c].borc += tutar; taraflar[c].borcAdet += 1; }
  });

  // VADESİ GELEN (kullanıcı, 2026-09-28): KAPANMAMIŞ borcun vadesi BUGÜN ya da
  // daha önce dolan kısmı. Kapanış SUNUCUDAN gelir (`borclari_kapat`, FIFO
  // mahsup) — aşağıdaki tablonun "Gecikti/Kısmi/Bekliyor" durumlarıyla AYNI
  // kaynak; ayrı hesaplasaydık kutu ile tablo farklı rakam söylerdi. Kısmi
  // kapanan satırda yalnızca KALAN sayılır. Vade tarihi yoksa fatura tarihi
  // esas alınır (peşin sayılır) — cari listesindeki vade dağılımıyla aynı kural.
  // Bugün vadesi dolan da dahil: "gecikmiş" değildir ama ödenmesi gerekir.
  const vadesiGelen = {};
  ledger.forEach(e => {
    if (e.type !== 'debt') return;
    const c = e.currency || 'TRY';
    if (vadesiGelen[c] === undefined) vadesiGelen[c] = 0;
    if (e.kapandi === true) return;
    const vade = e.due_date || e.invoice_date || '';
    if (vade && vade <= now) {
      vadesiGelen[c] += e.kalan_borc != null ? e.kalan_borc : Math.abs(e.amount_foreign || 0);
    }
  });

  // Eski→yeni sırala (running balance hesabı için)
  const sortedLedger = [...ledger].sort((a, b) => {
    const d = (a.invoice_date || '').localeCompare(b.invoice_date || '');
    return d !== 0 ? d : (a.created_at || '').localeCompare(b.created_at || '');
  });

  const filteredLedger = sortedLedger.filter(e => {
    if (currFilter !== 'all' && e.currency !== currFilter) return false;
    if (paidFilter === 'paid' && !e.is_paid) return false;
    if (paidFilter === 'unpaid' && e.is_paid) return false;
    return true;
  });

  // Kümülatif bakiye: eski→yeni hesapla
  const runningBalances = {};
  const ledgerWithBalance = filteredLedger.map(e => {
    const c = e.currency || 'TRY';
    if (runningBalances[c] === undefined) runningBalances[c] = 0;
    runningBalances[c] += (e.is_incoming ? 1 : -1) * (e.amount_foreign || 0);
    return { ...e, runningBalance: runningBalances[c] };
  });

  // Ekranda yeni→eski göster
  const ledgerDisplayed = [...ledgerWithBalance].reverse();

  // Döküm EKRANDAKİ süzgeçle sınırlı: kullanıcı ne görüyorsa belgede o çıkar.
  // Hangi süzgeçle alındığı belgenin başlığına yazılır, yoksa eksik bir ekstre
  // "hesabın tamamı" sanılabilir.
  const ekstreAl = (bicim) => ekstreIndir({
    yol: `/suppliers/${id}/ekstre`,
    bicim,
    satirIdler: filteredLedger.map(e => e.id),
    filtre: [
      `Para birimi: ${currFilter === 'all' ? 'Tümü' : currFilter}`,
      `Durum: ${paidFilter === 'all' ? 'Tümü' : paidFilter === 'paid' ? 'Ödenmiş' : 'Ödenmemiş'}`,
    ].join(' · '),
    ad: supplier?.name,
  });

  const addInvoice = async () => {
    setLoading(true);
    try {
      await api.post(`/suppliers/${id}/ledger`, { ...invoiceForm, type: 'debt', amount_foreign: parseFloat(invoiceForm.amount_foreign), exchange_rate: parseFloat(invoiceForm.exchange_rate) });
      toast.success('Fatura eklendi');
      setShowInvoice(false);
      setInvoiceForm({ invoice_number: '', invoice_date: '', due_date: '', currency: 'TRY', amount_foreign: '', exchange_rate: '1', notes: '' });
      fetchData();
    } catch (err) { toast.error('Hata'); }
    setLoading(false);
  };


  const addBankAccount = async () => {
    setLoading(true);
    try {
      await api.post(`/suppliers/${id}/bank-accounts`, bankForm);
      toast.success('Banka hesabi eklendi');
      setShowBank(false);
      setBankForm({ bank_name: '', currency: 'TRY', iban: '', uban: '', account_holder: '' });
      fetchData();
    } catch (err) { toast.error('Hata'); }
    setLoading(false);
  };

  const deleteBankAccount = async (accountId) => {
    if (!window.confirm('Hesabi silmek istiyor musunuz?')) return;
    await api.delete(`/suppliers/${id}/bank-accounts/${accountId}`).catch(() => {});
    fetchData();
  };

  // Profil baş harfleri
  const initials = supplier.name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const hasAddress = supplier.address_street || supplier.address_city || supplier.address;
  // netBalance: is_incoming toplamı − is_outgoing toplamı (basit net)
  const netBalance = balances;

  return (
    <div data-testid="supplier-detail-page">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1916] tracking-tight">{supplier.name}</h1>
          <p className="text-xs text-[#8a7e70] font-mono">{supplier.code}</p>
        </div>
        {/* Cari notu: firma adının yanında. Not yoksa yalnızca yazma yetkisi olana
            "Not Ekle" düğmesi çıkar; varsa herkes görür, yetkili kalem simgesiyle düzenler. */}
        {supplier.cari_notu ? (
          <div className="flex items-start gap-2 min-w-0 max-w-xl rounded-md border border-[#e0cf8a] bg-[#fbf4d6] px-3 py-1.5 text-sm text-[#5a4a10]"
            data-testid="cari-notu"
            title={[supplier.cari_notu_guncelleyen,
              supplier.cari_notu_zaman && new Date(supplier.cari_notu_zaman).toLocaleString('tr-TR')]
              .filter(Boolean).join(' · ')}>
            <StickyNote size={14} className="mt-0.5 flex-shrink-0" strokeWidth={1.8} />
            <span className="whitespace-pre-wrap break-words min-w-0">{supplier.cari_notu}</span>
            {notYazabilir && (
              <button type="button" onClick={notAc} aria-label="Notu düzenle"
                className="mt-0.5 flex-shrink-0 text-[#7a6a2e] hover:text-[#1a1916]"
                data-testid="cari-notu-duzenle">
                <Pencil size={13} />
              </button>
            )}
          </div>
        ) : notYazabilir && (
          <Button type="button" size="sm" variant="outline" onClick={notAc}
            className="border-[#d0c4b0] text-[#5a554e] h-8 px-3 text-xs"
            data-testid="cari-not-ekle">
            <StickyNote size={13} className="mr-1.5" /> Not Ekle
          </Button>
        )}
      </div>

      <Tabs defaultValue="ledger" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="ledger" data-testid="tab-ledger">Cari Hesap</TabsTrigger>
          <TabsTrigger value="info" data-testid="tab-info">Genel Bilgiler</TabsTrigger>
        </TabsList>

        {/* ─── TAB: Info ─── */}
        <TabsContent value="info" className="space-y-4 animate-fade-in">

          {/* Profil Kartı */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0] overflow-hidden">
            <div className="h-1.5 w-full bg-gradient-to-r from-[#2e2d29] via-[#7a6a2e] to-[#3d6e4e]" />
            <CardContent className="p-5 flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-[#2e2d29] flex items-center justify-center flex-shrink-0 shadow-md">
                <span className="text-[#f5f0e8] font-extrabold text-lg tracking-tight">{initials}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 min-w-0">
                  <h2 className="text-xl font-extrabold text-[#1a1916] tracking-tight truncate">{supplier.name}</h2>
                  <KopyalaDugmesi deger={supplier.name} etiket="Firma adı" testId="kopyala-firma-adi" />
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="font-mono text-xs bg-[#e8dfd0] text-[#5a554e] px-2 py-0.5 rounded-md">{supplier.code}</span>
                  {supplier.account_type && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#2e2d29]/8 text-[#2e2d29]">
                      <BadgeCheck size={10} strokeWidth={2} /> {supplier.account_type}
                    </span>
                  )}
                </div>
              </div>
              {/* Bakiye özeti + Düzenle butonu */}
              <div className="hidden md:flex flex-col items-end gap-2">
                {Object.keys(netBalance).length > 0 && (
                  <div className="flex flex-col items-end gap-1">
                    {Object.entries(netBalance).map(([curr, bal]) => (
                      <div key={curr} className="text-right">
                        <span className={`text-lg font-extrabold ${bal > 0.005 ? 'text-[#3d6e4e]' : bal < -0.005 ? 'text-[#7a3535]' : 'text-[#1a1916]'}`}>
                          {fmtTR(bal)}
                        </span>
                        <span className="text-xs text-[#8a7e70] ml-1">{curr}</span>
                      </div>
                    ))}
                    <span className="text-[10px] text-[#8a7e70]">Net Bakiye</span>
                  </div>
                )}
                {canEdit && !editMode && (
                  <Button size="sm" onClick={startEdit}
                    className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold h-8 px-3 text-xs">
                    <Pencil size={13} className="mr-1.5" /> Düzenle
                  </Button>
                )}
                {editMode && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditMode(false)}
                      className="border-[#d0c4b0] h-8 px-3 text-xs">
                      <X size={13} className="mr-1" /> İptal
                    </Button>
                    <Button size="sm" onClick={saveEdit} disabled={loading}
                      className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold h-8 px-3 text-xs">
                      <Save size={13} className="mr-1" /> {loading ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* İki kolonlu bilgi kartları */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* İletişim */}
            <Card className="bg-[#faf7f2] border-[#d0c4b0]">
              <CardHeader className="pb-3 border-b border-[#e8dfd0]">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-[#3d6e4e]/8 flex items-center justify-center">
                    <Phone size={13} className="text-[#3d6e4e]" strokeWidth={1.8} />
                  </div>
                  <CardTitle className="text-sm font-bold text-[#1a1916]">İletişim</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {editMode ? (
                  <div className="space-y-3">
                    <EditField label="Ad / Unvan" value={editForm.name} onChange={v => setEditForm(p => ({ ...p, name: v }))} />
                    <EditField label="Kod" value={editForm.code} onChange={v => setEditForm(p => ({ ...p, code: v }))} />
                    <EditField label="Telefon" value={editForm.phone} onChange={v => setEditForm(p => ({ ...p, phone: v }))} />
                    <EditField label="Yetkili Tel" value={editForm.authorized_phone} onChange={v => setEditForm(p => ({ ...p, authorized_phone: v }))} />
                    <EditField label="E-posta" value={editForm.email} onChange={v => setEditForm(p => ({ ...p, email: v }))} />
                    <EditField label="Adres (Sokak)" value={editForm.address_street} onChange={v => setEditForm(p => ({ ...p, address_street: v }))} />
                    <EditField label="İlçe" value={editForm.address_district} onChange={v => setEditForm(p => ({ ...p, address_district: v }))} />
                    <EditField label="Şehir" value={editForm.address_city} onChange={v => setEditForm(p => ({ ...p, address_city: v }))} />
                    <EditField label="Ülke" value={editForm.address_country} onChange={v => setEditForm(p => ({ ...p, address_country: v }))} />
                  </div>
                ) : (
                  <dl className="space-y-3">
                    <InfoRow label="Telefon" value={supplier.phone} icon={<Phone size={12} className="text-[#8a7e70]" />}
                      ek={<WhatsAppDugmesi telefon={supplier.phone} />} />
                    <InfoRow label="Yetkili Tel" value={supplier.authorized_phone} icon={<Phone size={12} className="text-[#8a7e70]" />}
                      ek={<WhatsAppDugmesi telefon={supplier.authorized_phone} />} />
                    <InfoRow label="E-posta" value={supplier.email} icon={<Mail size={12} className="text-[#8a7e70]" />} />
                    {hasAddress && (
                      <div className="pt-2 mt-1 border-t border-[#e8dfd0]">
                        <div className="flex items-start gap-2">
                          <MapPin size={13} className="text-[#8a7e70] mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-[#1a1916]">
                            {supplier.address_street && <p>{supplier.address_street}</p>}
                            <p>{[supplier.address_district, supplier.address_city].filter(Boolean).join(', ')}{supplier.address_country && <span className="text-[#8a7e70]"> / {supplier.address_country}</span>}</p>
                            {!supplier.address_street && !supplier.address_city && <p className="text-[#8a7e70]">{supplier.address}</p>}
                          </div>
                        </div>
                      </div>
                    )}
                  </dl>
                )}
              </CardContent>
            </Card>

            {/* Vergi */}
            <Card className="bg-[#faf7f2] border-[#d0c4b0]">
              <CardHeader className="pb-3 border-b border-[#e8dfd0]">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-[#7a6a2e]/8 flex items-center justify-center">
                    <Shield size={13} className="text-[#7a6a2e]" strokeWidth={1.8} />
                  </div>
                  <CardTitle className="text-sm font-bold text-[#1a1916]">Vergi & Kimlik</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {editMode ? (
                  <div className="space-y-3">
                    <EditField label="Vergi Dairesi" value={editForm.tax_office} onChange={v => setEditForm(p => ({ ...p, tax_office: v }))} />
                    <EditField label="Vergi No" value={editForm.tax_number} onChange={v => setEditForm(p => ({ ...p, tax_number: v }))} mono />
                    <div>
                      <p className="text-xs font-semibold text-[#8a7e70] mb-1">Hesap Türü</p>
                      <Select value={editForm.account_type} onValueChange={v => setEditForm(p => ({ ...p, account_type: v }))}>
                        <SelectTrigger className="bg-white border-[#d0c4b0] h-9 text-sm"><SelectValue placeholder="Seçin" /></SelectTrigger>
                        <SelectContent>{accountTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <dl className="space-y-3">
                    <InfoRow label="Vergi Dairesi" value={supplier.tax_office} />
                    <InfoRow label="Vergi No" value={supplier.tax_number} mono />
                    <InfoRow label="Hesap Türü" value={supplier.account_type} />
                  </dl>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Banka Hesapları */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="pb-3 border-b border-[#e8dfd0] flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#2e6fa0]/8 flex items-center justify-center">
                  <Landmark size={13} className="text-[#2e6fa0]" strokeWidth={1.8} />
                </div>
                <CardTitle className="text-sm font-bold text-[#1a1916]">Banka Hesapları</CardTitle>
              </div>
              <Button size="sm" onClick={() => setShowBank(true)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="add-bank-btn">
                <Plus size={13} className="mr-1.5" /> Hesap Ekle
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Banka</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">PB</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">IBAN</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Hesap Sahibi</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(supplier.bank_accounts || []).map(a => (
                    <TableRow key={a.id} className="erp-table-row border-[#d0c4b0]">
                      <TableCell className="font-medium text-[#1a1916]">{a.bank_name}</TableCell>
                      <TableCell className="text-sm font-semibold">{a.currency}</TableCell>
                      {/* IBAN panoya BOŞLUKSUZ gider: banka ekranlarının çoğu boşluklu
                          yapıştırmayı ya reddediyor ya da uzunluk sınırına takılıyor */}
                      <TableCell className="font-mono text-sm text-[#3F3E39]">
                        <span className="inline-flex items-center gap-1">
                          {a.iban || a.uban || '-'}
                          <KopyalaDugmesi deger={(a.iban || a.uban || '').replace(/\s/g, '')} etiket="IBAN"
                            testId={`kopyala-iban-${a.id}`} />
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-[#3F3E39]">
                        <span className="inline-flex items-center gap-1">
                          {a.account_holder || '-'}
                          <KopyalaDugmesi deger={a.account_holder} etiket="Hesap sahibi"
                            testId={`kopyala-hesap-sahibi-${a.id}`} />
                        </span>
                      </TableCell>
                      <TableCell>
                        {bankaSilebilir && (
                        <Button variant="ghost" size="icon" onClick={() => deleteBankAccount(a.id)}
                          className="h-8 w-8 text-[#8a7e70] hover:text-[#7a3535]"><Trash2 size={13} /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!(supplier.bank_accounts || []).length && (
                    <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#8a7e70] text-sm">Banka hesabı tanımlanmamış</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── TAB: Ledger ─── */}
        <TabsContent value="ledger" className="space-y-6">
          {/* Balance summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(balances).map(([curr, net]) => (
              <Popover key={curr}>
              <PopoverTrigger asChild>
              <Card className="bg-[#faf7f2] border-[#d0c4b0] cursor-pointer hover:border-[#8a7e70] transition-colors"
                title="Toplam borç ve alacak için tıklayın" data-testid={`bakiye-kutusu-${curr}`}>
                <CardContent className="p-4 text-center">
                  <p className="text-xs font-bold uppercase text-[#8a7e70] mb-2">{curr} Bakiye</p>
                  <p className={`text-xl font-bold ${net > 0.005 ? 'text-[#3d6e4e]' : net < -0.005 ? 'text-[#7a3535]' : 'text-[#1a1916]'}`}>
                    {fmtTR(net)}
                  </p>
                  {/* Yalnızca o para biriminde BORÇ kaydı olan hesapta: kasa/banka ya da
                      müşteri hesabında "vadesi gelen 0,00" gürültü olurdu. */}
                  {vadesiGelen[curr] !== undefined && (
                    <p className="text-[11px] text-[#8a7e70] mt-1.5"
                      title="Kapanmamış borcun vadesi bugün ya da daha önce dolan kısmı (vade tarihi yoksa fatura tarihi)"
                      data-testid={`vadesi-gelen-${curr}`}>
                      Vadesi gelen:{' '}
                      <span className={`font-mono font-semibold ${vadesiGelen[curr] > 0.005 ? 'text-[#7a3535]' : 'text-[#3F3E39]'}`}>
                        {fmtTR(vadesiGelen[curr])}
                      </span>
                    </p>
                  )}
                </CardContent>
              </Card>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3 bg-white border-[#d0c4b0]" data-testid={`bakiye-balonu-${curr}`}>
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e70] mb-2">{curr} hareket toplamları</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-[#3F3E39]">Toplam Borç <span className="text-[10px] text-[#8a7e70]">({taraflar[curr]?.borcAdet || 0} kayıt)</span></span>
                    <span className="font-mono font-semibold text-[#7a3535]">{fmtTR(taraflar[curr]?.borc || 0)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-[#3F3E39]">Toplam Alacak <span className="text-[10px] text-[#8a7e70]">({taraflar[curr]?.alacakAdet || 0} kayıt)</span></span>
                    <span className="font-mono font-semibold text-[#3d6e4e]">{fmtTR(taraflar[curr]?.alacak || 0)}</span>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-[#e8dfd0] pt-1.5">
                    <span className="font-semibold text-[#1a1916]">Bakiye</span>
                    <span className={`font-mono font-bold ${net > 0.005 ? 'text-[#3d6e4e]' : net < -0.005 ? 'text-[#7a3535]' : 'text-[#1a1916]'}`}>{fmtTR(net)}</span>
                  </div>
                </div>
                <p className="text-[10px] text-[#8a7e70] mt-2">Bakiye = Alacak − Borç. Hesabın bütün hareketlerinden; tablodaki süzgeçler uygulanmaz.</p>
              </PopoverContent>
              </Popover>
            ))}
          </div>

          {/* Filters + Actions */}
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex gap-3">
              <Select value={currFilter} onValueChange={setCurrFilter}>
                <SelectTrigger className="w-28 bg-white border-[#d0c4b0]"><SelectValue placeholder="PB" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tumu</SelectItem>
                  {currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={paidFilter} onValueChange={setPaidFilter}>
                <SelectTrigger className="w-32 bg-white border-[#d0c4b0]"><SelectValue placeholder="Durum" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tumu</SelectItem>
                  <SelectItem value="unpaid">Odenmemis</SelectItem>
                  <SelectItem value="paid">Odenmis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => ekstreAl('xlsx')}
                className="border-[#d0c4b0] text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
                title="Ekrandaki ekstreyi Excel olarak indir" data-testid="ekstre-excel">
                <FileSpreadsheet size={14} className="mr-1" /> Excel
              </Button>
              <Button size="sm" variant="outline" onClick={() => ekstreAl('pdf')}
                className="border-[#d0c4b0] text-[#7a3535] hover:bg-[#7a3535]/10"
                title="Ekrandaki ekstreyi PDF olarak indir" data-testid="ekstre-pdf">
                <FileDown size={14} className="mr-1" /> PDF
              </Button>
              {/* Tedarikçiye girişsiz ekstre linki (1 gün, güncel veri). Para birimi
                  süzgeci linke işlenir; ödeme durumu süzgeci işlenmez — tedarikçi
                  hesabın tamamını görür. */}
              <WhatsAppGonder modul="cari_ekstre" kayitId={id} etiket="WhatsApp"
                ek={currFilter !== 'all' ? { pb: currFilter } : undefined} testId="ekstre-whatsapp" />
              <Button size="sm" onClick={() => navigate(`/erp/invoices/new?supplier=${id}`)} className="bg-[#7a3535] text-white hover:bg-[#5e2929]" data-testid="add-invoice-btn">
                <Plus size={14} className="mr-1" /> Yeni Fatura
              </Button>
              <Button size="sm" onClick={() => setShowPayment(true)} className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e]" data-testid="add-payment-btn">
                <Plus size={14} className="mr-1" /> Odeme Kaydet
              </Button>
            </div>
          </div>

          {/* Ledger table */}
          <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 w-24">Tarih</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 w-20">Vade</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 w-8">PB</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 text-right w-28">Tutar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 text-right w-28">Bakiye</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1">Açıklama</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1">Karşı Hesap</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 w-16">Tür</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-[9px] uppercase py-1 w-16">Durum</TableHead>
                  <TableHead className="w-24 py-1"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledgerDisplayed.map(e => {
                  // Kapanis SUNUCUDAN gelir (`borclari_kapat`): odemeler en
                  // eski borctan dusulur. Satirdaki `is_paid` bayragi odeme
                  // geldiginde GUNCELLENMIYOR; ona bakan eski kural, bakiyesi
                  // sifir olan cariye "Gecikti"/"Bekliyor" dedirtiyordu.
                  const acik = e.type === 'debt' && e.kapandi !== true;
                  const kismi = acik && e.kalan_borc != null
                    && e.kalan_borc < Math.abs(e.amount_foreign) - 0.005;
                  const overdue = acik && e.due_date && e.due_date < now;
                  return (
                    <TableRow key={e.id} className={`erp-table-row border-[#d0c4b0] ${overdue ? 'bg-[#7a3535]/5' : ''}`}>
                      <TableCell className="text-[10px] text-[#8a7e70] font-mono py-1 whitespace-nowrap">{e.invoice_date || '-'}</TableCell>
                      <TableCell className={`text-[10px] font-mono py-1 whitespace-nowrap ${overdue ? 'text-[#7a3535] font-semibold' : 'text-[#8a7e70]'}`}>
                        {e.due_date || '-'}
                        {overdue && <AlertTriangle size={9} className="inline ml-0.5 text-[#7a3535]" />}
                      </TableCell>
                      <TableCell className="text-[10px] font-semibold py-1">{e.currency}</TableCell>
                      <TableCell className={`text-right font-mono font-semibold text-[10px] py-1 whitespace-nowrap ${e.is_incoming ? 'text-[#3d6e4e]' : 'text-[#7a3535]'}`}>
                        {e.is_incoming ? '+' : '-'}{fmtTR(e.amount_foreign)}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-semibold text-[10px] py-1 whitespace-nowrap ${e.runningBalance > 0.005 ? 'text-[#3d6e4e]' : e.runningBalance < -0.005 ? 'text-[#7a3535]' : 'text-[#1a1916]'}`}>
                        {fmtTR(e.runningBalance)}
                      </TableCell>
                      <TableCell className="text-[10px] text-[#1a1916] py-1 max-w-[180px] truncate" title={e.notes || ''}>
                        {e.notes || <span className="text-[#c0b8ad]">—</span>}
                      </TableCell>
                      <TableCell className="text-[10px] text-[#3d6e4e] py-1 max-w-[130px] truncate" title={e.counterpart_name || e.payment_account_name || ''}>
                        {e.counterpart_name || e.payment_account_name || <span className="text-[#c0b8ad]">—</span>}
                      </TableCell>
                      <TableCell className="py-1">
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${e.type === 'debt' ? 'bg-[#7a3535]/10 text-[#7a3535]' : e.type === 'iade' ? 'bg-[#b06a00]/10 text-[#b06a00]' : 'bg-[#3d6e4e]/10 text-[#3d6e4e]'}`}>
                          {e.type === 'debt' ? 'Borç' : e.type === 'iade' ? 'İade' : 'Ödeme'}
                        </span>
                      </TableCell>
                      <TableCell className="py-1">
                        {e.type === 'payment' || e.type === 'iade' || !acik ? (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#3d6e4e]/10 text-[#3d6e4e]">{e.type === 'iade' ? 'İade' : 'Ödendi'}</span>
                        ) : kismi ? (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#b06a00]/10 text-[#b06a00]"
                            title={`Kalan: ${fmtTR(e.kalan_borc)} ${e.currency}`}>Kısmi</span>
                        ) : overdue ? (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#7a3535]/10 text-[#7a3535]">Gecikti</span>
                        ) : (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#7a6a2e]/10 text-[#7a6a2e]">Bekliyor</span>
                        )}
                      </TableCell>
                      {/* Fatura linki */}
                      <TableCell>
                        {e.type === 'debt' && e.invoice_id ? (
                          <Link to={`/erp/invoices/${e.invoice_id}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-[#e8dfd0] transition-colors text-[#8a7e70] hover:text-[#1a1916]"
                            title="Faturayı görüntüle">
                            <ExternalLink size={13} />
                          </Link>
                        ) : e.type === 'debt' && e.invoice_number && !e.bilnex_modul && !/^(MAAS-|DEVIR)/.test(e.invoice_number) ? (
                          // Bağsız borçta numarayla arama yalnızca FATURADAN doğmuş
                          // olabilecek satırda anlamlı: Bilnex dekont/kasa kaydı (Nisan
                          // ücret tahakkuku "9" gibi), maaş tahakkuku ve devir açılışı
                          // fatura değildir, arama boş liste döndürürdü.
                          <Link to={`/erp/invoices?supplier=${id}&search=${e.invoice_number}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-[#e8dfd0] transition-colors text-[#8a7e70] hover:text-[#1a1916]"
                            title="Faturayı ara">
                            <FileText size={13} />
                          </Link>
                        ) : null}
                        {/* Dekont yalnızca para hareketinde anlamlı; borç kaydı (fatura,
                            maaş tahakkuku) bir ödeme değil */}
                        {e.type !== 'debt' && (
                          <button type="button" onClick={() => dekontuAc(e.id, false)}
                            title="Ödeme dekontunu görüntüle" aria-label="Ödeme dekontu"
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-[#8a7e70] hover:text-[#2e6fa0] hover:bg-[#2e6fa0]/10 transition-colors"
                            data-testid={`ekstre-dekont-${e.id}`}>
                            <Receipt size={12} />
                          </button>
                        )}
                        {e.type !== 'debt' && (
                          <EkDugmesi hareketId={e.id} sayi={e.ek_sayisi} onDegisti={fetchData} />
                        )}
                        {duzenlenebilir(e) && (
                          <span className="inline-flex items-center">
                            <button type="button" onClick={() => hareketiAc(e)}
                              title="Hareketi düzenle" aria-label="Hareketi düzenle"
                              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-[#8a7e70] hover:text-[#1a1916] hover:bg-[#e8dfd0] transition-colors"
                              data-testid={`ekstre-duzenle-${e.id}`}>
                              <Pencil size={12} />
                            </button>
                            {hareketSilebilir && (
                            <button type="button" onClick={() => hareketiSil(e)}
                              title="Hareketi sil" aria-label="Hareketi sil"
                              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-[#8a7e70] hover:text-[#7a3535] hover:bg-[#7a3535]/10 transition-colors"
                              data-testid={`ekstre-sil-${e.id}`}>
                              <Trash2 size={12} />
                            </button>
                            )}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredLedger.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-[#8a7e70]">Kayıt bulunamadı</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Cari notu — suppliers/edit yetkisi */}
      <Dialog open={notAcik} onOpenChange={setNotAcik}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">
              {supplier.cari_notu ? 'Notu Düzenle' : 'Not Ekle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <textarea value={notMetni} onChange={e => setNotMetni(e.target.value)}
              maxLength={500} rows={4} autoFocus
              placeholder="Bu cari hakkında not…"
              className="w-full rounded-md border border-[#d0c4b0] bg-white px-3 py-2 text-sm text-[#1a1916] focus:outline-none focus:ring-2 focus:ring-[#7a6a2e]/40"
              data-testid="cari-notu-metin" />
            <p className="text-[11px] text-[#8a7e70] flex justify-between">
              <span>{supplier.cari_notu ? 'Boş bırakıp kaydederseniz not silinir.' : ' '}</span>
              <span>{notMetni.length}/500</span>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotAcik(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={notKaydet} disabled={notKaydediliyor || (!supplier.cari_notu && !notMetni.trim())}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="cari-notu-kaydet">
              {notKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hareket düzenleme — treasury/edit yetkisi */}
      <Dialog open={!!editEntry} onOpenChange={(a) => !a && setEditEntry(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">
              Hareketi Düzenle
              <span className="ml-2 text-xs font-normal text-[#8a7e70]">
                {editEntry?.type === 'debt' ? 'Borç' : editEntry?.type === 'iade' ? 'İade' : 'Ödeme'}
              </span>
            </DialogTitle>
          </DialogHeader>
          {editEntry && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#3F3E39] text-xs">Tarih</Label>
                  <Input type="date" value={entryForm.transaction_date}
                    onChange={e => setEntryForm(p => ({ ...p, transaction_date: e.target.value }))}
                    className="bg-white border-[#d0c4b0]" data-testid="ekstre-tarih" />
                </div>
                <div>
                  <Label className="text-[#3F3E39] text-xs">Vade</Label>
                  <Input type="date" value={entryForm.due_date}
                    onChange={e => setEntryForm(p => ({ ...p, due_date: e.target.value }))}
                    className="bg-white border-[#d0c4b0]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#3F3E39] text-xs">Tutar</Label>
                  <Input value={entryForm.amount}
                    onChange={e => setEntryForm(p => ({ ...p, amount: e.target.value }))}
                    className="bg-white border-[#d0c4b0] text-right font-mono" data-testid="ekstre-tutar" />
                </div>
                <div>
                  <Label className="text-[#3F3E39] text-xs">Para Birimi</Label>
                  <select value={entryForm.currency}
                    onChange={e => setEntryForm(p => ({ ...p, currency: e.target.value }))}
                    className="w-full h-10 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm focus:outline-none">
                    {['TRY', 'USD', 'EUR', 'GBP'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              {/* İki taraf da HER HESAPTAN seçilebilir (kasa/banka, personel,
                  cari): şirket adına personelin yaptığı ödemede çıkan hesap
                  personeldir. Bir dönem yalnızca kasa/banka listeleniyordu. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#3d6e4e] text-xs">Giren hesap (parayı alan)</Label>
                  <SearchableSelect value={entryForm.giren} options={hesapSecenekleri}
                    onChange={v => setEntryForm(p => ({ ...p, giren: v }))}
                    placeholder="Seçin…" testId="ekstre-giren-hesap" />
                </div>
                <div>
                  <Label className="text-[#7a3535] text-xs">Çıkan hesap (parayı veren)</Label>
                  <SearchableSelect value={entryForm.cikan} options={hesapSecenekleri}
                    onChange={v => setEntryForm(p => ({ ...p, cikan: v }))}
                    placeholder="Seçin…" testId="ekstre-cikan-hesap" />
                </div>
              </div>
              <p className="-mt-1 text-[10px] text-[#8a7e70]">
                Bir taraf boş bırakılırsa hareket tek taraflı kalır (ör. karşı hesabı
                bilinmeyen ödeme). Hareket her iki hesabın ekstresinde de değişir.
              </p>
              <div>
                <Label className="text-[#3F3E39] text-xs">Açıklama</Label>
                <Input value={entryForm.description}
                  onChange={e => setEntryForm(p => ({ ...p, description: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" data-testid="ekstre-aciklama" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)} className="border-[#d0c4b0]">Vazgeç</Button>
            <Button onClick={hareketiKaydet} disabled={entrySaving}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="ekstre-kaydet">
              <Save size={14} className="mr-1.5" /> {entrySaving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={showInvoice} onOpenChange={setShowInvoice}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Yeni Fatura (Borc)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-[#3F3E39]">Fatura No</Label>
                <Input value={invoiceForm.invoice_number} onChange={(e) => setInvoiceForm(p => ({ ...p, invoice_number: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              <div className="space-y-2"><Label className="text-[#3F3E39]">Para Birimi</Label>
                <Select value={invoiceForm.currency} onValueChange={(v) => setInvoiceForm(p => ({ ...p, currency: v, exchange_rate: v === 'TRY' ? '1' : p.exchange_rate }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                  <SelectContent>{currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-[#3F3E39]">Fatura Tarihi</Label>
                <Input type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm(p => ({ ...p, invoice_date: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              <div className="space-y-2"><Label className="text-[#3F3E39]">Vade Tarihi</Label>
                <Input type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm(p => ({ ...p, due_date: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-[#3F3E39]">Tutar ({invoiceForm.currency})</Label>
                <Input type="number" step="0.01" value={invoiceForm.amount_foreign} onChange={(e) => setInvoiceForm(p => ({ ...p, amount_foreign: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              {invoiceForm.currency !== 'TRY' && (
                <div className="space-y-2"><Label className="text-[#3F3E39]">Kur (1 {invoiceForm.currency} = ? TL)</Label>
                  <Input type="number" step="0.0001" value={invoiceForm.exchange_rate} onChange={(e) => setInvoiceForm(p => ({ ...p, exchange_rate: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              )}
            </div>
            {invoiceForm.amount_foreign && invoiceForm.exchange_rate && (
              <p className="text-sm text-[#8a7e70]">TL Karsiligi: <span className="font-bold text-[#1a1916]">{(parseFloat(invoiceForm.amount_foreign || 0) * parseFloat(invoiceForm.exchange_rate || 1)).toFixed(2)} TL</span></p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoice(false)} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={addInvoice} disabled={loading} className="bg-[#7a3535] text-white hover:bg-[#5e2929]">
              {loading ? 'Kaydediliyor...' : 'Fatura Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <NewTransactionModal
        open={showPayment}
        onClose={() => setShowPayment(false)}
        accounts={treasuryAccounts}
        suppliers={allSuppliers}
        defaultSupplierId={id}
        onSaved={() => fetchData()}
      />

      {/* Bank Account Dialog */}
      <Dialog open={showBank} onOpenChange={setShowBank}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Banka Hesabi Ekle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-[#3F3E39]">Banka Adi</Label>
                <Input value={bankForm.bank_name} onChange={(e) => setBankForm(p => ({ ...p, bank_name: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              <div className="space-y-2"><Label className="text-[#3F3E39]">Para Birimi</Label>
                <Select value={bankForm.currency} onValueChange={(v) => setBankForm(p => ({ ...p, currency: v }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                  <SelectContent>{currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-2"><Label className="text-[#3F3E39]">IBAN</Label>
              <Input value={bankForm.iban} onChange={(e) => setBankForm(p => ({ ...p, iban: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
            <div className="space-y-2"><Label className="text-[#3F3E39]">Hesap Sahibi</Label>
              <Input value={bankForm.account_holder} onChange={(e) => setBankForm(p => ({ ...p, account_holder: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBank(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={addBankAccount} disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ label, value, mono, icon, ek }) {
  return (
    <div className="flex items-start justify-between gap-4 py-0.5">
      <dt className="text-xs font-semibold text-[#8a7e70] flex-shrink-0 min-w-[100px]">{label}</dt>
      <dd className={`text-sm text-right flex items-center gap-1.5 ${mono ? 'font-mono text-[#3F3E39]' : 'text-[#1a1916] font-medium'}`}>
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {value || <span className="text-[#8a7e70] font-normal">—</span>}
        {value && ek}
      </dd>
    </div>
  );
}

function EditField({ label, value, onChange, mono }) {
  return (
    <div>
      <p className="text-xs font-semibold text-[#8a7e70] mb-1">{label}</p>
      <Input
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className={`bg-white border-[#d0c4b0] h-9 text-sm ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}
